import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { DataTable } from '../../components/intelligence/DataTable';
import { FilterBar, createFilterState, filtersToParams } from '../../components/intelligence/FilterBar';
import { KPICard } from '../../components/intelligence/KPICard';
import { SkeletonKPI, SkeletonTable } from '../../components/intelligence/SkeletonLoader';
import { Button } from '../../components/ui';

interface AuditEvent {
  id: string;
  action: string;
  actor_id: string;
  actor_name: string;
  target: string;
  ip: string;
  timestamp: string;
  suspicious: boolean;
}

const loading = signal(true);
const error = signal('');
const events = signal<AuditEvent[]>([]);
const totalEvents = signal(0);
const suspiciousCount = signal(0);
const loginAttempts = signal(0);
const activeSessions = signal(0);
const filters = createFilterState();
const activeTab = signal<'all' | 'suspicious' | 'logins'>('all');

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const params = filtersToParams(filters.value);
    params.set('event_category', 'audit');
    params.set('limit', '100');
    const res = await api.get<any>(`/intelligence/events?${params.toString()}`);
    const evts = (res.events || []).map((e: any) => ({
      id: e.id,
      action: e.event_name || e.event_type || 'unknown',
      actor_id: e.actor_id || e.customer_id || '',
      actor_name: e.metadata?.actor_name || e.actor_id || 'Sistema',
      target: e.metadata?.target || '',
      ip: e.metadata?.ip || e.ip_address || '',
      timestamp: e.created_at || e.timestamp,
      suspicious: e.metadata?.suspicious === true,
    }));
    events.value = evts;
    totalEvents.value = res.total || evts.length;
    suspiciousCount.value = evts.filter((e: AuditEvent) => e.suspicious).length;
    loginAttempts.value = evts.filter((e: AuditEvent) => e.action.includes('login')).length;
    activeSessions.value = 0;
  } catch (err: any) {
    error.value = err.message || 'Erro ao carregar auditoria';
    events.value = [];
  } finally {
    loading.value = false;
  }
}

function exportCSV() {
  const header = 'Timestamp,Action,Actor,Target,IP,Suspicious\n';
  const rows = events.value.map((e) =>
    `${e.timestamp},${e.action},${e.actor_name},${e.target},${e.ip},${e.suspicious}`
  ).join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AuditPanel() {
  useEffect(() => { loadData(); }, []);

  const filtered = activeTab.value === 'suspicious'
    ? events.value.filter((e) => e.suspicious)
    : activeTab.value === 'logins'
    ? events.value.filter((e) => e.action.includes('login'))
    : events.value;

  return (
    <div>
      <div class="flex items-center justify-between mb-4">
 <h2 class="text-xl font-bold text-white">Auditoria</h2>
        <Button variant="ghost"
          class="px-3 py-1.5 bg-gray-800 text-gray-300 text-xs rounded-lg hover:bg-gray-700 transition-colors"
          onClick={exportCSV}
        >
 Exportar CSV
        </Button>
      </div>

      <FilterBar filters={filters} onApply={loadData} showTool={false} />

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">{error.value}</div>
      )}

      {/* KPIs */}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {loading.value ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
        ) : (
          <>
 <KPICard icon="" label="Total Eventos" value={totalEvents.value} />
 <KPICard icon="" label="Suspeitos" value={suspiciousCount.value} />
 <KPICard icon="" label="Tentativas Login" value={loginAttempts.value} />
 <KPICard icon="" label="Sessões Ativas" value={activeSessions.value} />
          </>
        )}
      </div>

      {/* Tabs */}
      <div class="flex gap-2 mb-4">
        {[
          { v: 'all' as const, label: 'Todos' },
 { v: 'suspicious' as const, label: 'Suspeitos' },
 { v: 'logins' as const, label: 'Logins' },
        ].map(({ v, label }) => (
          <Button variant="ghost"
            key={v}
            class={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTab.value === v ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => { activeTab.value = v; }}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Table */}
      {loading.value ? <SkeletonTable rows={8} /> : (
        <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700 [&_th]:!bg-gray-800 [&_th]:!text-gray-400 [&_td]:!text-gray-300 [&_tr:hover]:!bg-gray-750">
          <DataTable
            columns={[
              { key: 'timestamp', label: 'Data/Hora', render: (r: AuditEvent) => new Date(r.timestamp).toLocaleString('pt-BR') },
              { key: 'action', label: 'Ação' },
              { key: 'actor_name', label: 'Ator' },
              { key: 'target', label: 'Alvo' },
              { key: 'ip', label: 'IP' },
              { key: 'suspicious', label: 'Status', render: (r: AuditEvent) =>
 r.suspicious ? <span class="text-red-400 font-medium">Suspeito</span> : <span class="text-green-400"></span>
              },
            ]}
            data={filtered}
            pageSize={20}
            emptyMessage="Nenhum evento de auditoria"
          />
        </div>
      )}
    </div>
  );
}
